import { PoGrid } from '@/components/grid/PoGrid';
import { FunctionComponent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowDoubleClickedEvent,
  SortChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { EventModel, EventModelFieldNames } from '@/modules/events/types/EventModel';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { useTranslation } from '@/lib';
import { ColumnID } from '@/components/grid/column-ids';
import { ARCHIVED_LOCATION_NAME } from '@/modules/locations/api/locations/location.contracts';
import { formatGpsAccuracy, GpsAccuracyLevelCellRenderer } from '@/components/grid/cells/GpsAccuracyCellRenderer';
import { TrackerTypeCellRenderer, TrackerTypeCellRendererParams } from '@/components/grid/cells/TrackerTypeCellRenderer';
import { trackerTypeToString } from '@/modules/trackers/lib/tracker.utils';

interface AssetEventsGridProps {
  data?: EventModel[];
  isError: boolean;
  onExportToExcelClicked: (gridApi: GridApi<EventModel> | undefined) => void;
  onSortChanged?: (event: SortChangedEvent) => void;
  onGridReady?: (event: GridReadyEvent) => void;
  isLoading: boolean;
}

export const AssetEventsGrid: FunctionComponent<AssetEventsGridProps> = ({
  data,
  isError,
  onExportToExcelClicked,
  onSortChanged,
  onGridReady,
  isLoading,
}) => {
  const { t } = useTranslation();
  if (!data && !isLoading) {
    return <div>{t('no_data')}</div>;
  }
  const navigate = useNavigate();

  const customGridOptions: GridOptions<EventModel> = {
    onRowDoubleClicked(event: RowDoubleClickedEvent<EventModel, unknown>) {
      navigate(`/app/events/${event.data?.id}`);
    },
    getRowId: (params) => params.data.id.toString(),
    onSortChanged,
    onGridReady,
    alwaysMultiSort: false,
    getContextMenuItems: (params) => [
      'copy',
      'separator',
      {
        name: t('export_excel'),
        action: () => onExportToExcelClicked(params.api),
      },
    ],
  };

  const columns: ColDefOrGroup<EventModel>[] = useMemo(
    () => [
      {
        colId: ColumnID.ASSET_CODE,
        headerName: t('event.asset_code'),
        field: EventModelFieldNames.AssetCode,
      },
      {
        colId: ColumnID.OCCURRED_AT,
        field: EventModelFieldNames.OccurredAt,
        headerName: t('event.occurred_at'),
        cellRenderer: RelativeDateCellRenderer,
      },
      {
        colId: ColumnID.LOCATION_NAME,
        field: EventModelFieldNames.LocationName,
        headerName: t('event.location'),
        cellRenderer: LinkCellRenderer,
        cellRendererParams: (params: ICellRendererParams<EventModel>): LinkCellRendererParams => ({
          pathname: params.data?.location.name === ARCHIVED_LOCATION_NAME ? undefined : `/app/locations/${params.data?.location.id}`,
        }),
      },
      {
        colId: ColumnID.TRACKER_TYPE,
        field: EventModelFieldNames.TrackerType,
        headerName: t('event.tracker_type'),
        cellRenderer: TrackerTypeCellRenderer,
        cellRendererParams: (params: ICellRendererParams<EventModel>): TrackerTypeCellRendererParams => ({
          trackerType: params.data?.tracker?.type ?? null,
        }),
        valueFormatter: (params: ValueFormatterParams<EventModel>) => {
          return trackerTypeToString(params.data?.tracker?.type, t) ?? '';
        },
      },
      {
        colId: ColumnID.EVENT_TYPE,
        headerName: t('event.type.title'),
        valueGetter: (params: ValueGetterParams<EventModel>) => {
          return t(`event.type.${params.data?.type}`);
        },
      },
      {
        colId: ColumnID.EVENT_SOURCE,
        headerName: 'Source',
        valueGetter: (params: ValueGetterParams<EventModel>) => {
          return t(`enums.event_source.${params.data?.eventSource}`);
        },
      },
      {
        colId: ColumnID.GPS_ACCURACY,
        headerName: t('event.coordinates_accuracy'),
        field: EventModelFieldNames.GpsAccuracyLevel,
        cellRenderer: GpsAccuracyLevelCellRenderer,
        valueFormatter: (params: ValueFormatterParams<EventModel>) => {
          if (params.data?.gpsAccuracyLevel) return formatGpsAccuracy(params.data?.gpsAccuracyLevel, t);
          else {
            return t('unknown');
          }
        },
      },
      {
        colId: ColumnID.GPS_SOURCE_TYPE,
        headerName: t('event.coordinates_source'),
        field: EventModelFieldNames.GpsSourceType,
        valueGetter: (params: ValueGetterParams<EventModel>) => {
          return t(`event.gps_source.${params.data?.gpsSourceType}`);
        },
      },
    ],
    [t],
  );

  return (
    <div className="flex h-full flex-1 flex-grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid isLoading={isLoading} colDefs={columns} rowData={data} gridOptions={customGridOptions} disableDefaultGridOptions />
      )}
    </div>
  );
};
